// src/context/AuthContext.js
/**
 * Global authentication context.
 *
 * Exposes:
 *   • `user`    Firebase user object (or `null` when signed-out).
 *   • `loading` Flag while auth state is being resolved on boot.
 *
 * Implementation notes:
 *   1. `onAuthStateChanged` fires once on mount and whenever the user's
 *      authentication state changes.
 *   2. The user object is *optimistically* set as soon as Firebase returns it.
 *      We then **enrich** the object with a `returning` flag based on Cloud
 *      Storage metadata, without blocking the UI.
 *   3. `contextValue` is memoised to prevent needless re-renders.
 */

import React, {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { getAuth, onAuthStateChanged } from "firebase/auth";
import { getStorage, listAll, ref } from "firebase/storage";
import { firebaseApp } from "../firebase/firebaseApp";

// Provide sensible defaults to avoid undefined checks in consumers.
const AuthContext = createContext({
  user: null,
  loading: true,
});

/**
 * React provider that supplies `user` and `loading` to its subtree.
 */
export function AuthProvider({ children }) {
  // Firebase SDK factories are idempotent; they return singletons.
  const auth = getAuth(firebaseApp);

  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  /* ------------------------------------------------------------------ */
  /* Subscribe to Firebase auth state once on mount.                    */
  /* ------------------------------------------------------------------ */
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (firebaseUser) => {
      if (!firebaseUser) {
        setUser(null);
        setLoading(false);
        return;
      }

      // Expose bare user immediately for snappy UI updates.
      setUser(firebaseUser);
      setLoading(false);

      // Fetch a "returning" flag asynchronously.
      try {
        const storage = getStorage(firebaseApp);
        const metadataRef = ref(storage, `user_metadata/${firebaseUser.uid}`);
        const res = await listAll(metadataRef);
        const returning = res.items.length > 0;

        // Merge flag into existing user object without breaking refs.
        setUser((prev) => ({ ...prev, returning }));
      } catch (err) {
        if (process.env.NODE_ENV === "development") {
          console.error("Error checking returning metadata:", err);
        }
        setUser((prev) => ({ ...prev, returning: false }));
      }
    });

    // Cleanup listener on unmount.
    return unsubscribe;
  }, [auth]);

  /* ------------------------------------------------------------------ */
  /* Memoise context to keep reference stable between renders.          */
  /* ------------------------------------------------------------------ */
  const contextValue = useMemo(() => ({ user, loading }), [user, loading]);

  return (
    <AuthContext.Provider value={contextValue}>
      {/* Delay rendering children until first auth resolution completes. */}
      {!loading && children}
    </AuthContext.Provider>
  );
}

/**
 * Convenience hook for consuming the authentication context.
 *
 * @returns {{ user: import('firebase/auth').User | null, loading: boolean }}
 */
export const useAuth = () => useContext(AuthContext);
